/**
 * useReceiptPdf.ts — Hook para descargar/compartir un nodo DOM como PDF.
 *
 * QUÉ HACE:
 *   - Recibe un ref a un elemento (típicamente el ticket renderizado).
 *   - Captura ese nodo como canvas (vía html2canvas) y lo empaqueta como PDF
 *     usando jsPDF.
 *   - Expone tres acciones:
 *       · downloadPdf(ref)     → descarga local el PDF
 *       · generatePdfBlob(ref) → devuelve { blob, filename } para uso externo
 *       · sharePdf(ref)        → intenta navigator.share con el PDF; cae a download
 *
 * QUÉ EXPORTA:
 *   - useReceiptPdf({ filename }) → { downloadPdf, generatePdfBlob, sharePdf, isGenerating, canShareFiles }
 *
 * QUÉ NO HACE:
 *   - No conoce de WhatsApp, ni de recibos, ni de caja.
 *   - No depende de backend ni de mocks.
 *
 * REGLAS:
 *   - jsPDF/html2canvas se importan con dynamic import para no inflar el bundle.
 *   - Tamaño del PDF: ancho 80mm (térmico) o A4 según opciones.
 *   - canShareFiles solo es true si el browser soporta navigator.canShare con archivos.
 */

import { logger } from "@/lib/logger";
import { useState, useCallback, useMemo, RefObject } from "react";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { captureReceiptCanvas, cssPxToMm } from "@/lib/utils/receiptCapture";

interface UseReceiptPdfOptions {
  /** Nombre del archivo sin extensión */
  filename?: string;
  /** Formato: "thermal" (58/80mm), "viewport" (ancho del ticket web) o "a4". */
  format?: "thermal" | "a4" | "viewport";
  /** Ancho de papel térmico cuando format === "thermal". */
  paperWidthMm?: 58 | 80;
}

interface PdfBlobResult {
  blob: Blob;
  filename: string;
}

interface UseReceiptPdfReturn {
  /** Genera y descarga el PDF a partir del ref proporcionado. */
  downloadPdf: (ref: RefObject<HTMLElement>) => Promise<boolean>;
  /** Genera el PDF y devuelve el Blob + filename (no descarga). */
  generatePdfBlob: (
    ref: RefObject<HTMLElement>,
  ) => Promise<PdfBlobResult | null>;
  /**
   * Intenta compartir el PDF vía navigator.share (móvil → menú nativo a
   * WhatsApp/Email/etc). Si no es posible, descarga el archivo.
   */
  sharePdf: (
    ref: RefObject<HTMLElement>,
    shareTitle?: string,
    shareText?: string,
  ) => Promise<boolean>;
  /** True mientras se genera el PDF. */
  isGenerating: boolean;
  /** True si el browser soporta compartir archivos vía Web Share API. */
  canShareFiles: boolean;
}

// Detección capacidad de compartir archivos. Probe-friendly y resiliente.
const detectCanShareFiles = (): boolean => {
  // En Capacitor native usamos los plugins oficiales (@capacitor/share +
  // @capacitor/filesystem) que sí soportan archivos en iOS/Android.
  if (Capacitor.isNativePlatform()) return true;
  if (typeof navigator === "undefined") return false;
  if (!("share" in navigator) || !("canShare" in navigator)) return false;
  try {
    const probe = new File([new Blob(["x"])], "probe.pdf", {
      type: "application/pdf",
    });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== "string") return reject(new Error("read fail"));
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function writeBlobToNativeCache(
  blob: Blob,
  filename: string,
): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const data = await blobToBase64(blob);
    const res = await Filesystem.writeFile({
      path: filename,
      data,
      directory: Directory.Cache,
    });
    return res.uri;
  } catch (err) {
    logger.error("[useReceiptPdf writeBlobToNativeCache] error:", err);
    return null;
  }
}

async function nativeSaveAsDownload(
  blob: Blob,
  name: string,
): Promise<boolean> {
  try {
    const data = await blobToBase64(blob);
    try {
      await Filesystem.writeFile({
        path: name,
        data,
        directory: Directory.Documents,
      });
    } catch {
      await Filesystem.writeFile({
        path: name,
        data,
        directory: Directory.Cache,
      });
    }
    return true;
  } catch (err) {
    logger.error("[useReceiptPdf nativeSaveAsDownload] error:", err);
    return false;
  }
}

function downloadBlobInBrowser(blob: Blob, filename: string): boolean {
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch (err) {
    logger.error("[useReceiptPdf.downloadBlobInBrowser] error:", err);
    return false;
  }
}

export function useReceiptPdf({
  filename = "recibo",
  format = "thermal",
  paperWidthMm = 80,
}: UseReceiptPdfOptions = {}): UseReceiptPdfReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const canShareFiles = useMemo(detectCanShareFiles, []);

  // Lógica común de captura → blob PDF
  const generatePdfBlob = useCallback(
    async (ref: RefObject<HTMLElement>): Promise<PdfBlobResult | null> => {
      if (!ref.current || isGenerating) return null;
      setIsGenerating(true);
      try {
        const jspdfMod = await import("jspdf");
        const { jsPDF } = jspdfMod;

        const { canvas, cssWidth, cssHeight } = await captureReceiptCanvas(
          ref.current,
          {
            backgroundColor: "#ffffff",
          },
        );
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const aspectRatio = cssHeight / cssWidth;

        let pageWidthMm: number;
        let pageHeightMm: number;
        if (format === "viewport") {
          pageWidthMm = cssPxToMm(cssWidth);
          pageHeightMm = cssPxToMm(cssHeight);
        } else if (format === "thermal") {
          pageWidthMm = paperWidthMm;
          pageHeightMm = pageWidthMm * aspectRatio;
        } else {
          pageWidthMm = 210;
          pageHeightMm = pageWidthMm * aspectRatio;
        }

        const pdf = new jsPDF({
          orientation: pageHeightMm > pageWidthMm ? "portrait" : "landscape",
          unit: "mm",
          format: format === "a4" ? "a4" : [pageWidthMm, pageHeightMm],
        });

        if (format === "a4") {
          const usableWidth = 180;
          const usableHeight = usableWidth * aspectRatio;
          pdf.addImage(
            imgData,
            "JPEG",
            15,
            15,
            usableWidth,
            usableHeight,
            undefined,
            "FAST",
          );
        } else {
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            0,
            pageWidthMm,
            pageHeightMm,
            undefined,
            "FAST",
          );
        }

        const blob = pdf.output("blob") as Blob;
        const safeFilename = filename.endsWith(".pdf")
          ? filename
          : `${filename}.pdf`;
        return { blob, filename: safeFilename };
      } catch (err) {
        logger.error("[useReceiptPdf.generatePdfBlob] error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    [filename, format, paperWidthMm, isGenerating],
  );

  const downloadPdf = useCallback(
    async (ref: RefObject<HTMLElement>): Promise<boolean> => {
      const result = await generatePdfBlob(ref);
      if (!result) return false;
      if (Capacitor.isNativePlatform()) {
        return nativeSaveAsDownload(result.blob, result.filename);
      }
      return downloadBlobInBrowser(result.blob, result.filename);
    },
    [generatePdfBlob],
  );

  const sharePdf = useCallback(
    async (
      ref: RefObject<HTMLElement>,
      shareTitle?: string,
      shareText?: string,
    ): Promise<boolean> => {
      const result = await generatePdfBlob(ref);
      if (!result) return false;

      // Native: usar plugin oficial (@capacitor/share) con URI del archivo.
      if (Capacitor.isNativePlatform()) {
        const uri = await writeBlobToNativeCache(result.blob, result.filename);
        if (!uri) {
          return nativeSaveAsDownload(result.blob, result.filename);
        }
        try {
          await Share.share({
            title: shareTitle,
            text: shareText,
            url: uri,
            dialogTitle: shareTitle,
          });
          return true;
        } catch (err) {
          logger.warn("[useReceiptPdf.sharePdf native] canceled:", err);
          return false;
        }
      }

      const file = new File([result.blob], result.filename, {
        type: "application/pdf",
      });
      // Intentar Web Share API
      try {
        if (
          typeof navigator !== "undefined" &&
          "canShare" in navigator &&
          navigator.canShare({ files: [file] }) &&
          "share" in navigator
        ) {
          await navigator.share({
            files: [file],
            title: shareTitle,
            text: shareText,
          });
          return true;
        }
      } catch (err) {
        // El usuario canceló o falló: caer al download.
        logger.warn("[useReceiptPdf.sharePdf] share canceled:", err);
        return false;
      }
      // Fallback: descarga local
      return downloadBlobInBrowser(result.blob, result.filename);
    },
    [generatePdfBlob],
  );

  return {
    downloadPdf,
    generatePdfBlob,
    sharePdf,
    isGenerating,
    canShareFiles,
  };
}
